/**
 * After the rave (step 108, the user, 2026-11-02): the two pushes land here — `?ask=review` ("Rate MyRave") and
 * `?donate=1` ("Support MyRave"). Only the address is read in the first download; the dialogs (with the Stripe form)
 * load when one is asked for (`AfterRaveSheets.tsx`). The parameter leaves the address once read, so a reload never
 * asks again.
 */
import { lazy, Suspense, useState } from 'react';

type Ask = 'review' | 'donate';

/** Which ask the address carries (pure). */
export function askIn(search: string): Ask | null {
  const q = new URLSearchParams(search);
  if (q.get('ask') === 'review') return 'review';
  if (q.get('donate') === '1') return 'donate';
  return null;
}

const Sheets = lazy(() => import('./AfterRaveSheets'));

/**
 * The ask this page load arrived with — kept here, not in a component's state: the app re-mounts this part of the page
 * while it starts (the session, the language), and the address no longer carries it by then (2026-11-02: the dialog
 * opened and vanished). It is forgotten only when the person closes the dialog.
 */
let pending: Ask | null | undefined;
function takeAsk(): Ask | null {
  if (pending === undefined) {
    pending = typeof window === 'undefined' ? null : askIn(window.location.search);
    if (pending) {
      const url = new URL(window.location.href);
      url.searchParams.delete('ask');
      url.searchParams.delete('donate');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }
  return pending;
}
/** Tests: start over as a fresh page load. */
export function resetAfterRaveForTests(): void { pending = undefined; }

export function AfterRave() {
  const [ask, setAsk] = useState<Ask | null>(() => takeAsk());
  if (!ask) return null;
  const close = () => { pending = null; setAsk(null); };
  return <Suspense fallback={null}><Sheets ask={ask} onClose={close} /></Suspense>;
}
